<script setup>
/**
 * The second of the two clicks. THE PLAN, §3 and §4 P11.
 *
 * A DIALOG, NOT AN INLINE FORM, and the pane's width is the whole argument: two text
 * boxes in a 320px column give an agent about forty characters a line to compose a reply
 * she is going to send to a customer. The dialog is `md`, centred, and the boxes are the
 * size of the thing being written. The inline alternative was drafted and it made the
 * cheap action look like the expensive one.
 *
 * THE QUESTION ARRIVES PRE-FILLED and stays editable. The BFF computes it from the
 * customer's own last message and normalises it exactly as the write will
 * (`routes/api/knowledge.ts` `suggestQuestion`) — so the value in the box is the value
 * that will be saved, and the form cannot open already failing its own validator. It is
 * editable because a customer's message is often a paragraph with a question buried in
 * it, and the question is the retrieval key: shortening it is the single highest-value
 * thing the agent can do here.
 *
 * WHAT IT REFUSES TO DO: pre-fill the ANSWER. Everything on the pane could suggest one —
 * the reply the assistant already gave is right there — and it would be wrong, because
 * this form exists precisely when that reply was wrong. A pre-filled answer is a default
 * that gets accepted, and the default here is the mistake.
 *
 * VALIDATION IS THE SERVER'S, SHOWN LOCALLY. The BFF answers `validation_failed` with a
 * `fields` map keyed by request-body key, which is an invariant in both directions
 * (`http-errors.ts`), so each sentence is pinned under the control that caused it with no
 * prose parsing and no second rule written here that could disagree with the first. Only
 * the emptiness check is local, and only so the button can be disabled rather than
 * round-tripping to be told what the agent can already see.
 *
 * EASE C3 (spec §6 P-6.1 to P-6.4) adds two modes, and a second step after the save.
 * `fix` is "Fix it" from an unanswered question, a chat turn or Overview: the same teach,
 * with the group of unanswered questions it answers (`gapId`) and what the assistant said
 * shown above the boxes. `edit` changes one answer from "All answers": free unless the
 * question changes (the question is what is searched, so a new one is one priced step,
 * `edit_question`). After either, the dialog stays open on what the save did: the answers
 * it replaced, the ones that still say a different number ("Use mine instead" switches
 * that one off, "Keep both" leaves it), and "Check it now", one priced Test it message
 * (`check_answer`, O-3) that runs only on the tap and shows Before beside Now.
 */
import { computed, ref } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';

import WasloTextArea from './WasloTextArea.vue';
import { useWasloI18n } from '../i18n.js';
import AiConfirmDialog from './AiConfirmDialog.vue';
import { useAiConfirm } from '../composables/useAiConfirm.js';
import { WASLO_ERROR_CODES, wasloApi, withConfirm } from '../api/client.js';

const props = defineProps({
  accountId: { type: Number, default: null },
  conversationId: { type: Number, default: null },
  /** The customer's last message, already normalised and cut by the BFF. */
  suggestedQuestion: { type: String, default: '' },
  /**
   * P-1.7. `teach`: an administrator saves an answer, priced and confirmed. `suggest`: a team
   * member sends it to the owner, free; nothing is taught until the owner accepts it on Answers.
   */
  mode: {
    type: String,
    default: 'teach',
    validator: value => ['teach', 'suggest', 'fix', 'edit'].includes(value),
  },
  /** `fix`: the group of unanswered questions this answers (P-6.6), cleared on save. */
  gapId: { type: Number, default: null },
  /**
   * `fix`: what the assistant said, shown as Before. `null` is "it did not know", which is
   * what an unanswered question is.
   */
  itSaid: { type: String, default: null },
  /** `edit`: the answer being changed. Its words are Before. */
  answerId: { type: Number, default: null },
  initialAnswer: { type: String, default: '' },
});

const emit = defineEmits(['taught', 'suggested', 'saved']);

const { t } = useWasloI18n();

// P-5.12 / O-9: saving a taught answer is one AI step, charged from now on. Save wears the
// price and asks first; Cancel in that dialog leaves this form exactly as it is.
// A suggestion is free, and a team member cannot read the owner's prices, so in `suggest`
// mode the controller has no account and never asks for them.
const teachCharge = useAiConfirm({
  // `fix` is the same priced teach (C3, P-6.4).
  accountId: computed(() =>
    props.mode === 'teach' || props.mode === 'fix' ? props.accountId : null
  ),
  op: 'teach_answer',
});
// P-6.1: a new question is one priced step; new words alone are free.
const editCharge = useAiConfirm({
  accountId: computed(() => (props.mode === 'edit' ? props.accountId : null)),
  op: 'edit_question',
});
// P-6.4 / O-3: "Check it now" is one Test it message, on a tap only.
const checkCharge = useAiConfirm({
  accountId: computed(() =>
    props.mode === 'fix' || props.mode === 'edit' ? props.accountId : null
  ),
  op: 'check_answer',
});
const isSuggest = computed(() => props.mode === 'suggest');
const isEdit = computed(() => props.mode === 'edit');
/** `fix` and `edit` stay open after the save, on what the save did. */
const hasAfterStep = computed(() => props.mode === 'fix' || isEdit.value);

const dialogRef = ref(null);
const question = ref('');
const answer = ref('');
const isSaving = ref(false);
/** Form-level failure — a network drop, a 500. Field-level lives in `fields`. */
const failure = ref('');
const fields = ref({});
/** `form`, then `saved` for `fix` and `edit`. */
const step = ref('form');
/** What the save did: `{ replaced, conflicts }`, and the conflicts she has decided. */
const outcomeOfSave = ref({ replaced: [], conflicts: [] });
const decided = ref(new Set());
const savedQuestion = ref('');
const afterLine = ref('');
const check = ref(null);
const isChecking = ref(false);
const checkFailure = ref('');

const questionChanged = computed(
  () => isEdit.value && question.value.trim() !== props.suggestedQuestion.trim()
);

const saveLabel = computed(() => {
  if (isSuggest.value) return t('WASLO.PANEL.SUGGEST.SEND');
  if (isEdit.value) {
    return questionChanged.value
      ? editCharge.label(t('WASLO.PANEL.TEACH.SAVE'))
      : t('WASLO.PANEL.TEACH.SAVE');
  }
  return teachCharge.label(t('WASLO.PANEL.TEACH.SAVE'));
});
const copy = computed(() => {
  if (isSuggest.value) {
    return {
      title: t('WASLO.PANEL.SUGGEST.TITLE'),
      body: t('WASLO.PANEL.SUGGEST.BODY'),
      saving: t('WASLO.PANEL.SUGGEST.SENDING'),
    };
  }
  if (isEdit.value) {
    return {
      title: t('WASLO.FIX.TITLE_EDIT'),
      body: t('WASLO.FIX.BODY_EDIT'),
      saving: t('WASLO.PANEL.TEACH.SAVING'),
    };
  }
  if (props.mode === 'fix') {
    return {
      title: t('WASLO.FIX.TITLE_FIX'),
      body: t('WASLO.FIX.BODY_FIX'),
      saving: t('WASLO.PANEL.TEACH.SAVING'),
    };
  }
  return {
    title: t('WASLO.PANEL.TEACH.TITLE'),
    body: t('WASLO.PANEL.TEACH.BODY'),
    saving: t('WASLO.PANEL.TEACH.SAVING'),
  };
});

/** Before, in the check: what it said, or the old words of the answer being edited. */
const before = computed(() => {
  if (isEdit.value) return props.initialAnswer;
  return props.itSaid ?? '';
});
const openConflicts = computed(() =>
  outcomeOfSave.value.conflicts.filter(row => !decided.value.has(row.id))
);

const canSave = computed(
  () => question.value.trim() !== '' && answer.value.trim() !== ''
);

/**
 * Opened fresh every time, deliberately: a half-typed answer left over from the previous
 * conversation is the worst possible default in a box whose contents get sent to
 * customers.
 */
const open = () => {
  question.value = props.suggestedQuestion;
  // `edit` is the one mode that starts from words she already saved; every other mode
  // keeps the rule above and never pre-fills the answer.
  answer.value = isEdit.value ? props.initialAnswer : '';
  failure.value = '';
  fields.value = {};
  isSaving.value = false;
  step.value = 'form';
  outcomeOfSave.value = { replaced: [], conflicts: [] };
  decided.value = new Set();
  afterLine.value = '';
  check.value = null;
  checkFailure.value = '';
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

/** The teach and `fix` save. `null` when she cancelled the price. */
const saveTeach = async () => {
  const outcome = await teachCharge.confirmAndRun({
    run: credits =>
      wasloApi.teachAnswer(
        props.accountId,
        {
          question: question.value,
          answer: answer.value,
          ...(props.conversationId === null
            ? {}
            : { conversationId: props.conversationId }),
          ...(props.mode === 'fix' && props.gapId !== null
            ? { gapId: props.gapId }
            : {}),
        },
        undefined,
        withConfirm(credits)
      ),
  });
  return outcome.cancelled ? null : outcome.result;
};

/** The `edit` save: only what changed, priced only when the question did. */
const saveEdit = async () => {
  const patch = {};
  if (questionChanged.value) patch.question = question.value;
  if (answer.value.trim() !== props.initialAnswer.trim()) {
    patch.answer = answer.value;
  }
  if (Object.keys(patch).length === 0) {
    close();
    return null;
  }
  if (!questionChanged.value) {
    return wasloApi.updateAnswer(props.accountId, props.answerId, patch);
  }
  const outcome = await editCharge.confirmAndRun({
    run: credits =>
      wasloApi.updateAnswer(
        props.accountId,
        props.answerId,
        patch,
        undefined,
        withConfirm(credits)
      ),
  });
  return outcome.cancelled ? null : outcome.result;
};

const save = async () => {
  if (isSaving.value || !canSave.value || props.accountId === null) return;
  isSaving.value = true;
  failure.value = '';
  fields.value = {};
  if (isSuggest.value) {
    try {
      const result = await wasloApi.suggestAnswer(
        props.accountId,
        props.conversationId,
        { question: question.value, answer: answer.value }
      );
      close();
      emit('suggested', result);
    } catch (error) {
      if (error?.fields) fields.value = error.fields;
      else if (error?.code === WASLO_ERROR_CODES.SUGGESTIONS_FULL) {
        const count = Number.isSafeInteger(error.count) ? error.count : 0;
        failure.value = t('WASLO.PANEL.SUGGEST.FULL', { count }, count);
      } else if (error?.status === 429) {
        failure.value = t('WASLO.PANEL.SUGGEST.TOO_MANY');
      } else failure.value = t('WASLO.PANEL.SUGGEST.FAILED');
    } finally {
      isSaving.value = false;
    }
    return;
  }
  try {
    const result = isEdit.value ? await saveEdit() : await saveTeach();
    if (result === null) return;
    if (hasAfterStep.value) {
      outcomeOfSave.value = {
        replaced: result.replaced ?? [],
        conflicts: result.conflicts ?? [],
      };
      savedQuestion.value = question.value.trim();
      step.value = 'saved';
      emit('saved', result);
      return;
    }
    close();
    // The pane owns the confirmation, not this dialog: it is still true after the dialog
    // has gone, and it is where the agent's eyes are once it has.
    emit('taught', result);
  } catch (error) {
    // `fields` is non-empty exactly when the BFF said `validation_failed`, and null for
    // every other code — so this needs no guard beyond the null check.
    if (error?.fields) fields.value = error.fields;
    else failure.value = t('WASLO.PANEL.TEACH.FAILED');
  } finally {
    isSaving.value = false;
  }
};

/** "Use mine instead": switch the other answer off. Free, and it can be switched back on. */
const useMine = async row => {
  try {
    await wasloApi.updateAnswer(props.accountId, row.id, { enabled: false });
    decided.value = new Set([...decided.value, row.id]);
    afterLine.value = t('WASLO.FIX.USED_MINE');
  } catch {
    afterLine.value = t('WASLO.FIX.FAILED');
  }
};

const keepBoth = row => {
  decided.value = new Set([...decided.value, row.id]);
};

const runCheck = async () => {
  if (isChecking.value || props.accountId === null) return;
  isChecking.value = true;
  checkFailure.value = '';
  try {
    const outcome = await checkCharge.confirmAndRun({
      run: credits =>
        wasloApi.checkAnswer(
          props.accountId,
          savedQuestion.value,
          undefined,
          withConfirm(credits)
        ),
    });
    if (!outcome.cancelled) check.value = outcome.result;
  } catch {
    checkFailure.value = t('WASLO.FIX.CHECK_FAILED');
  } finally {
    isChecking.value = false;
  }
};

const onConfirm = () => (step.value === 'saved' ? close() : save());

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="md"
    :title="copy.title"
    :description="copy.body"
    :confirm-button-label="
      step === 'saved'
        ? t('WASLO.FIX.DONE')
        : isSaving
          ? copy.saving
          : saveLabel
    "
    :cancel-button-label="t('WASLO.PANEL.TEACH.CANCEL')"
    :show-cancel-button="step !== 'saved'"
    :is-loading="isSaving"
    :disable-confirm-button="step === 'form' && (isSaving || !canSave)"
    :data-waslo-teach-dialog="mode"
    @confirm="onConfirm"
  >
    <div
      v-if="step === 'saved'"
      class="flex flex-col gap-4"
      data-waslo-fix-saved
    >
      <p class="mb-0 text-sm text-n-slate-12">{{ t('WASLO.FIX.SAVED') }}</p>
      <p
        v-for="row in outcomeOfSave.replaced"
        :key="`replaced-${row.id}`"
        class="mb-0 text-sm text-n-slate-11"
        dir="auto"
        data-waslo-fix-replaced
      >
        {{ t('WASLO.FIX.REPLACES', { source: row.sourceTitle }) }}
      </p>
      <div
        v-for="row in openConflicts"
        :key="`conflict-${row.id}`"
        class="flex flex-col gap-2 p-3 rounded-lg bg-n-amber-2"
        data-waslo-fix-conflict
      >
        <p class="mb-0 text-sm text-n-slate-12" dir="auto">
          {{
            t('WASLO.FIX.ALSO_SAYS', {
              source: row.sourceTitle,
              answer: row.answer,
            })
          }}
        </p>
        <div class="flex flex-wrap gap-2">
          <Button
            :label="t('WASLO.FIX.USE_MINE')"
            size="sm"
            data-waslo-fix-use-mine
            @click="useMine(row)"
          />
          <Button
            :label="t('WASLO.FIX.KEEP_BOTH')"
            size="sm"
            variant="faded"
            color="slate"
            data-waslo-fix-keep-both
            @click="keepBoth(row)"
          />
        </div>
      </div>
      <p
        v-if="afterLine"
        class="mb-0 text-sm text-n-slate-11"
        aria-live="polite"
      >
        {{ afterLine }}
      </p>

      <div class="flex flex-col gap-3" data-waslo-fix-check>
        <Button
          v-if="!check"
          :label="checkCharge.label(t('WASLO.FIX.CHECK'))"
          size="sm"
          variant="faded"
          color="slate"
          :is-loading="isChecking"
          data-waslo-fix-check-run
          @click="runCheck"
        />
        <div v-else class="grid gap-3 sm:grid-cols-2">
          <div class="flex flex-col gap-1 p-3 rounded-lg bg-n-alpha-1">
            <span class="text-xs font-medium text-n-slate-10">
              {{ t('WASLO.FIX.BEFORE') }}
            </span>
            <p
              class="mb-0 text-sm break-words whitespace-pre-line text-n-slate-11"
              dir="auto"
              data-waslo-fix-before
            >
              <bdi>{{ before || t('WASLO.FIX.NOTHING_SAID') }}</bdi>
            </p>
          </div>
          <div class="flex flex-col gap-1 p-3 rounded-lg bg-n-alpha-1">
            <span class="text-xs font-medium text-n-slate-10">
              {{ t('WASLO.FIX.NOW') }}
            </span>
            <p
              class="mb-0 text-sm break-words whitespace-pre-line text-n-slate-12"
              dir="auto"
              data-waslo-fix-now
            >
              <bdi>{{ check.now || t('WASLO.FIX.NOW_HANDOFF') }}</bdi>
            </p>
          </div>
        </div>
        <p v-if="checkFailure" class="mb-0 text-sm text-n-ruby-11">
          {{ checkFailure }}
        </p>
        <p
          v-if="checkCharge.receiptText.value"
          class="mb-0 text-xs text-n-slate-11"
        >
          {{ checkCharge.receiptText.value }}
        </p>
      </div>
    </div>

    <div v-else class="flex flex-col gap-4">
      <div
        v-if="mode === 'fix'"
        class="flex flex-col gap-1 p-3 rounded-lg bg-n-alpha-1"
        data-waslo-fix-it-said
      >
        <span class="text-xs font-medium text-n-slate-10">
          {{ t('WASLO.FIX.IT_SAID') }}
        </span>
        <p
          class="mb-0 text-sm break-words whitespace-pre-line text-n-slate-11"
          dir="auto"
        >
          <bdi>{{ itSaid || t('WASLO.FIX.NOTHING_SAID') }}</bdi>
        </p>
      </div>
      <Banner v-if="failure" color="ruby" data-waslo-teach-failure>
        {{ failure }}
      </Banner>

      <WasloTextArea
        v-model="question"
        :label="t('WASLO.PANEL.TEACH.QUESTION_LABEL')"
        :placeholder="t('WASLO.PANEL.TEACH.QUESTION_PLACEHOLDER')"
        :hint="t('WASLO.PANEL.TEACH.QUESTION_HINT')"
        :message="fields.question || ''"
        message-type="error"
        :disabled="isSaving"
        :max-length="500"
        min-height="3.5rem"
        max-height="10rem"
        autofocus
        data-waslo-teach-question
      />

      <WasloTextArea
        v-model="answer"
        :label="t('WASLO.PANEL.TEACH.ANSWER_LABEL')"
        :placeholder="t('WASLO.PANEL.TEACH.ANSWER_PLACEHOLDER')"
        :message="fields.answer || ''"
        message-type="error"
        :disabled="isSaving"
        :max-length="4000"
        min-height="7rem"
        max-height="18rem"
        data-waslo-teach-answer
      />
    </div>
  </Dialog>
  <AiConfirmDialog :controller="teachCharge" />
  <AiConfirmDialog :controller="editCharge" />
  <AiConfirmDialog :controller="checkCharge" />
</template>
